import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import * as React from "react";
import { Button, useRefresh } from "react-admin";
import sendRequest from "../../dataProvider/sendRequest";
import CustomButtonCancel from "../../unit/CustomButtonCancel";
import CustomButtonSubmit from "../../unit/CustomButtonSubmit";
import CustomDialogTitle from "../../unit/CustomDialogTitle";
import downloadFileFun from "../../unit/DownloadFileFun";
import useNotify from "../../unit/useNotify";
import DialogApplicationBaseInfo from "../DialogApplicationBaseInfo";
import CustomApplicationDialogTitle from "./CustomApplicationDialogTitle";

export default function CustomExpired2DeclineDialog({ ...props }) {
  const PREVIEW_STEPS = {
    save: "Saving data...",
    generate: "Generating file on server...",
    download: "Downloading to browser...",
  };
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { appId, record } = props;
  const [open, setOpen] = React.useState(false);
  const [previewTitle, setPreviewTitle] = React.useState("Preview");
  const [submitTitle, setSubmitTitle] = React.useState("Decline");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [previewProgress, setPreviewProgress] = React.useState(0);
  const [previewProgressLabel, setPreviewProgressLabel] = React.useState("");
  const previewProgressRef = React.useRef(0);
  const generateProgressTimerRef = React.useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const downloadStartProgressRef = React.useRef(25);
  const refresh = useRefresh();

  React.useEffect(() => {
    previewProgressRef.current = previewProgress;
  }, [previewProgress]);

  const updatePreviewProgress = (progress: number, label: string) => {
    setPreviewProgress(progress);
    setPreviewProgressLabel(label);
  };

  const clearGenerateProgressTimer = () => {
    if (generateProgressTimerRef.current) {
      clearInterval(generateProgressTimerRef.current);
      generateProgressTimerRef.current = null;
    }
  };

  const startGenerateProgressTimer = () => {
    clearGenerateProgressTimer();
    generateProgressTimerRef.current = setInterval(() => {
      setPreviewProgress((prev) => {
        if (prev >= 84) {
          return prev;
        }

        const step = prev < 18 ? 0.8 : prev < 40 ? 1.2 : prev < 64 ? 0.9 : 0.45;

        return Math.min(84, Number((prev + step).toFixed(2)));
      });
    }, 500);
  };

  const resetPreviewLoadingState = () => {
    clearGenerateProgressTimer();
    setPreviewTitle("Preview");
    setIsPreviewLoading(false);
    setPreviewProgress(0);
    setPreviewProgressLabel("");
    downloadStartProgressRef.current = 25;
  };

  const resetSubmitState = () => {
    setSubmitTitle("Decline");
    setIsSubmitting(false);
  };

  React.useEffect(() => {
    return () => {
      clearGenerateProgressTimer();
    };
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetPreviewLoadingState();
    resetSubmitState();
    refresh();
  };

  const handlePreviewDecline = async ({ ...props }) => {
    if (isSubmitting) {
      return;
    }

    const { appId } = props;
    setIsPreviewLoading(true);
    setPreviewTitle("loading");
    updatePreviewProgress(8, PREVIEW_STEPS.save);
    const reqUrl = `applications/actions/expired/toDeclined`;
    const response = await sendRequest(reqUrl, {
      id: appId,
      data: {
        action: "preview",
      },
    });
    if (response && (response.status == "200" || response.code == "200")) {
      successNotify("Data saved successfully");
      updatePreviewProgress(18, PREVIEW_STEPS.generate);
      startGenerateProgressTimer();
      await downloadFileFun(
        appId,
        "decline_preview.pdf",
        "expired2decline_preview",
        "",
        {
          openInNewTab: false,
          onError: (message: string) => {
            errorNotify(message);
          },
          onStageChange: (stage) => {
            if (stage === "generating") {
              updatePreviewProgress(
                Math.max(previewProgressRef.current, 18),
                PREVIEW_STEPS.generate
              );
              startGenerateProgressTimer();
            }

            if (stage === "downloading") {
              clearGenerateProgressTimer();
              downloadStartProgressRef.current =
                previewProgressRef.current > 18
                  ? previewProgressRef.current
                  : 18;
              setPreviewProgressLabel(PREVIEW_STEPS.download);
            }
          },
          onDownloadProgress: (percent) => {
            if (typeof percent !== "number") {
              setPreviewProgress((prev) => Math.min(99, prev + 0.2));
              return;
            }

            const base = downloadStartProgressRef.current;
            const mapped = base + (percent / 100) * (100 - base);

            setPreviewProgress((prev) =>
              Math.max(prev, Math.min(100, Math.round(mapped)))
            );
          },
        }
      );
      clearGenerateProgressTimer();
      setPreviewProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 600));
      resetPreviewLoadingState();
    } else {
      errorNotify(response?.message || "Data not saved!");
      resetPreviewLoadingState();
    }
  };

  return (
    <React.Fragment>
      <Button
        id="btn-application-expired-decline"
        label="Decline"
        variant="contained"
        size="medium"
        color="error"
        sx={{
          marginLeft: "10px",
          width: "120px",
          "&:hover": { bgcolor: "gray" },
        }}
        onClick={handleClickOpen}
      />
      <Dialog
        id="dialog-application-expired-decline"
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="sm"
        PaperProps={{
          component: "form",
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isPreviewLoading || isSubmitting) {
              return;
            }

            setIsSubmitting(true);
            setSubmitTitle("loading");
            const reqUrl = `applications/actions/expired/toDeclined`;
            const response = await sendRequest(reqUrl, {
              id: appId,
              data: {},
            });
            if (
              response &&
              (response.status == "200" || response.code == "200")
            ) {
              successNotify("Data saved successfully");
              if (props?.cb) {
                props.cb("Declined");
              }
              handleClose();
            } else {
              errorNotify(response?.message || "Data not saved!");
              resetSubmitState();
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e5e5e5",
            mb: 2,
          }}
        >
          <CustomApplicationDialogTitle record={record} subTitle="Decline" />
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 0 }}>
          <DialogApplicationBaseInfo appId={appId} record={record} />
          <DialogContentText>
            Click on "DECLINE" button below to confirm moving App#{appId} to the
            "Declined" page. This action will trigger an Adverse Action letter
            sent to the applicant with the following reason: <br />
            You have not responded to our offer before the expiration date.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <CustomButtonCancel onClick={handleClose} />

          {!isPreviewLoading && (
            <Button
              label={previewTitle}
              endIcon={
                isPreviewLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              variant="text"
              color="warning"
              sx={{ width: "120px" }}
              size="medium"
              onClick={() => handlePreviewDecline({ appId })}
              disabled={isPreviewLoading || isSubmitting}
            />
          )}

          {isPreviewLoading && (
            <Box sx={{ width: "260px", ml: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {previewProgressLabel}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={previewProgress}
                  sx={{
                    flex: 1,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: "#bbdefb",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#1976d2",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#1565c0", minWidth: "40px" }}
                >
                  {Math.round(previewProgress)}%
                </Typography>
              </Box>
            </Box>
          )}
          <CustomButtonSubmit
            label={submitTitle}
            color="error"
            disabled={isPreviewLoading || isSubmitting}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
